import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { formatCny } from "@ledger/money";

const KEY_STORAGE = "ledger.kiosk-key";
const KEY_LENGTH = 12;

interface Problem {
  detail?: string;
  status?: number;
}

interface Product {
  id: string;
  name: string;
  price: string;
  trackInventory: boolean;
  stock: string;
}

interface Member {
  id: string;
  name: string;
  accountLabel: string;
  phoneLabel: string;
  balance: string;
}

interface Line {
  productId: string;
  name: string;
  price: bigint;
  quantity: number;
  stock: bigint | null;
}

type SaleStep = 0 | 1 | 2 | 3;

class KioskError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
  }
}

function readKey(): string {
  return localStorage.getItem(KEY_STORAGE) ?? "";
}

function newIdempotencyKey(): string {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

async function kiosk<T>(path: string, key: string, options: RequestInit = {}): Promise<T> {
  const headers = new Headers(options.headers);
  headers.set("x-kiosk-key", key);
  if (options.body) headers.set("content-type", "application/json");
  const response = await fetch(`/api/kiosk${path}`, { ...options, headers });
  if (!response.ok) {
    const problem = (await response.json().catch(() => ({}))) as Problem;
    throw new KioskError(problem.detail || "请求失败", response.status);
  }
  return (await response.json()) as T;
}

function money(value: bigint | string): string {
  return formatCny(typeof value === "bigint" ? value : BigInt(value));
}

function groupDigits(value: string): string {
  return value.replace(/(\d{4})(?=\d)/g, "$1 ");
}

function DigitPad({ onDigit, onDelete }: { onDigit: (digit: string) => void; onDelete: () => void }) {
  const keys = [
    { label: "1", action: "digit" },
    { label: "2", action: "digit" },
    { label: "3", action: "digit" },
    { label: "4", action: "digit" },
    { label: "5", action: "digit" },
    { label: "6", action: "digit" },
    { label: "7", action: "digit" },
    { label: "8", action: "digit" },
    { label: "9", action: "digit" },
    { label: "清空", action: "clear" },
    { label: "0", action: "digit" },
    { label: "删除", action: "delete" },
  ] as const;
  return (
    <div className="pad" onPointerDown={(event) => event.preventDefault()}>
      {keys.map((key) => (
        <button
          key={key.label}
          type="button"
          className={key.action === "digit" ? "key" : "key ghost"}
          aria-label={key.action === "clear" ? "清空，重新输入" : key.action === "delete" ? "删除最后一位" : `输入 ${key.label}`}
          onClick={() => {
            if (key.action === "delete") onDelete();
            else if (key.action === "clear") onDigit("");
            else onDigit(key.label);
          }}
        >
          {key.label}
        </button>
      ))}
    </div>
  );
}

const PHONE_LENGTH = 11;

function hasPhone(member: Member | null): boolean {
  return Boolean(member?.phoneLabel);
}

function groupPhone(value: string): string {
  return [value.slice(0, 3), value.slice(3, 7), value.slice(7, 11)].filter(Boolean).join(" ");
}

const CONFETTI_COLORS = ["#f43f5e", "#fb7185", "#f59e0b", "#facc15", "#22c55e", "#34d399", "#3b82f6", "#60a5fa", "#a855f7", "#f472b6"];

function CheckPop() {
  return (
    <svg className="check-pop" viewBox="0 0 52 52" aria-hidden="true">
      <circle className="check-ring" cx="26" cy="26" r="24" />
      <path className="check-mark" d="M14 27.5 22 35.5 38 17" />
    </svg>
  );
}

function Confetti() {
  const pieces = useMemo(() => Array.from({ length: 48 }, (_, index) => {
    const angle = (Math.PI * 2 * index) / 48 + (index % 3) * 0.15;
    const distance = 18 + (index % 8) * 7;
    return {
      x: Math.cos(angle) * distance,
      y: Math.sin(angle) * distance,
      r: (index * 53) % 360,
      delay: 0.78 + (index % 8) * 0.012,
      color: CONFETTI_COLORS[index % CONFETTI_COLORS.length] ?? "#f59e0b",
      w: 7 + (index % 4) * 4,
      h: 12 + (index % 5) * 7,
    };
  }), []);
  return (
    <div className="confetti">
      {pieces.map((piece, index) => (
        <span key={index} style={ribbonStyle(piece)} />
      ))}
    </div>
  );
}

function ribbonStyle(piece: { x: number; y: number; r: number; delay: number; color: string; w: number; h: number }): CSSProperties {
  return {
    width: piece.w,
    height: piece.h,
    background: piece.color,
    animationDelay: `${piece.delay}s`,
    ["--x" as string]: `${piece.x}vmin`,
    ["--y" as string]: `${piece.y}vmin`,
    ["--r" as string]: `${piece.r}deg`,
  };
}

function MemberMark({ member, hidePhone = false }: { member: Member; hidePhone?: boolean }) {
  const phone = hidePhone ? "" : member.phoneLabel || "未绑定手机";
  return (
    <div className="member-watermark" aria-hidden="true">
      <span className="watermark-name">{member.name}</span>
      {phone ? <span className="watermark-meta">{phone}</span> : null}
    </div>
  );
}

function Stage({ children }: { children: ReactNode }) {
  return (
    <div className="stage" role="application" aria-label="自助结账">
      {children}
    </div>
  );
}

export function App() {
  const [key, setKey] = useState(readKey);
  const [draft, setDraft] = useState("");
  const [terminalName, setTerminalName] = useState("");
  const [ready, setReady] = useState(false);
  const [notice, setNotice] = useState("");
  const [products, setProducts] = useState<Product[]>([]);
  const [digits, setDigits] = useState("");
  const [members, setMembers] = useState<Member[]>([]);
  const [memberLookup, setMemberLookup] = useState<"idle" | "loading" | "done">("idle");
  const [member, setMember] = useState<Member | null>(null);
  const [lines, setLines] = useState<Line[]>([]);
  const [step, setStep] = useState<SaleStep>(0);
  const [payCode, setPayCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [receipt, setReceipt] = useState<{ orderNumber: string; payAmount: string; balance: string } | null>(null);
  const [returnLeft, setReturnLeft] = useState(10);
  const [clearing, setClearing] = useState(false);
  const [clearDraft, setClearDraft] = useState("");
  const sessionRequest = useRef<object | null>(null);

  function forgetSavedKey(message: string) {
    localStorage.removeItem(KEY_STORAGE);
    setKey("");
    setDraft("");
    setClearDraft("");
    setClearing(false);
    setTerminalName("");
    setProducts([]);
    setNotice(message);
    setReady(true);
    resetSale();
  }

  function noteError(error: unknown, fallback: string) {
    if (error instanceof KioskError && error.status === 401) {
      forgetSavedKey("终端密钥已删除或失效，请重新设置");
      return;
    }
    setNotice(error instanceof Error ? error.message : fallback);
  }

  useEffect(() => {
    if (!key) {
      setReady(true);
      return;
    }
    const request = {};
    sessionRequest.current = request;
    void kiosk<{ name: string }>("/session", key)
      .then(async (session) => {
        if (sessionRequest.current !== request) return;
        const catalog = await kiosk<{ items: Product[] }>("/products", key);
        if (sessionRequest.current !== request) return;
        setTerminalName(session.name);
        setProducts(catalog.items);
        setReady(true);
      })
      .catch((error: unknown) => {
        if (sessionRequest.current !== request) return;
        setReady(true);
        noteError(error, "无法连接自助结账");
      });
    return () => {
      if (sessionRequest.current === request) sessionRequest.current = null;
    };
  }, [key]);

  useEffect(() => {
    if (!key || !terminalName || digits.length < 4 || member) {
      setMembers([]);
      setMemberLookup("idle");
      return;
    }
    setMemberLookup("loading");
    const timer = window.setTimeout(() => {
      void kiosk<{ items: Member[] }>(`/members?suffix=${digits}`, key)
        .then((result) => {
          setMembers(result.items);
          setMemberLookup("done");
          setNotice("");
        })
        .catch((error: unknown) => {
          setMembers([]);
          setMemberLookup("idle");
          noteError(error, "查找会员失败");
        });
    }, 180);
    return () => window.clearTimeout(timer);
  }, [digits, key, member, terminalName]);

  const total = useMemo(() => lines.reduce((sum, line) => sum + line.price * BigInt(line.quantity), 0n), [lines]);
  const count = lines.reduce((sum, line) => sum + line.quantity, 0);
  const balance = member ? BigInt(member.balance) : 0n;
  const shortfall = Boolean(member) && count > 0 && balance < total;
  const canCheckout = Boolean(member) && count > 0 && !shortfall;
  const quantityByProduct = useMemo(() => {
    const map = new Map<string, number>();
    for (const line of lines) map.set(line.productId, line.quantity);
    return map;
  }, [lines]);

  function resetSale() {
    setDigits("");
    setMembers([]);
    setMemberLookup("idle");
    setMember(null);
    setLines([]);
    setStep(0);
    setPayCode("");
    setReceipt(null);
    setReturnLeft(10);
    setNotice("");
  }

  useEffect(() => {
    if (!receipt) return;
    const started = Date.now();
    setReturnLeft(10);
    const timer = window.setInterval(() => {
      const remain = Math.max(0, 10 - Math.floor((Date.now() - started) / 1000));
      setReturnLeft(remain);
      if (remain === 0) {
        window.clearInterval(timer);
        resetSale();
      }
    }, 200);
    return () => window.clearInterval(timer);
  }, [receipt]);

  function go(next: SaleStep) {
    if (next === step) return;
    if (next > 0 && !member) return;
    if (next > 1 && count === 0) return;
    if (next > 2 && (!canCheckout || !hasPhone(member))) return;
    if (next === 0) setLines([]);
    if (next !== 3) setPayCode("");
    setStep(next);
  }

  function addProduct(product: Product) {
    const stock = product.trackInventory ? BigInt(product.stock) : null;
    if (stock !== null && stock <= 0n) return;
    setLines((current) => {
      const existing = current.find((line) => line.productId === product.id);
      if (!existing) {
        return [...current, { productId: product.id, name: product.name, price: BigInt(product.price), quantity: 1, stock }];
      }
      if (stock !== null && BigInt(existing.quantity) >= stock) return current;
      return current.map((line) => line.productId === product.id ? { ...line, quantity: line.quantity + 1 } : line);
    });
  }

  function changeQuantity(productId: string, next: number) {
    setLines((current) => current.flatMap((line) => {
      if (line.productId !== productId) return [line];
      if (next <= 0) return [];
      const capped = line.stock !== null && BigInt(next) > line.stock ? Number(line.stock) : next;
      return [{ ...line, quantity: capped }];
    }));
  }

  async function checkout() {
    if (!member || lines.length === 0 || submitting || shortfall || !/^1\d{10}$/.test(payCode)) return;
    setSubmitting(true);
    try {
      const result = await kiosk<{ orderNumber: string; payAmount: string; balance: string }>("/orders", key, {
        method: "POST",
        headers: { "idempotency-key": newIdempotencyKey() },
        body: JSON.stringify({
          userId: member.id,
          phone: payCode,
          items: lines.map((line) => ({ productId: line.productId, quantity: line.quantity })),
        }),
      });
      setReceipt(result);
    } catch (error) {
      noteError(error, "结账失败");
    } finally {
      setSubmitting(false);
    }
  }

  if (!ready) {
    return (
      <Stage>
        <section className="solo">
          <h1>正在连接</h1>
          <p>正在核对这台终端并加载商品。</p>
        </section>
      </Stage>
    );
  }

  if (!key || clearing) {
    const value = clearing ? clearDraft : draft;
    const setValue = clearing ? setClearDraft : setDraft;
    return (
      <Stage>
        <section className="solo">
          <p className="kicker">{clearing ? terminalName || "自助结账" : "自助结账"}</p>
          <h1>{clearing ? "再次输入密钥" : "设置终端密钥"}</h1>
          <p className="lead">{clearing ? "与本机保存的密钥一致后才会清除。" : "输入 12 位终端密钥，只保存在这台设备上。"}</p>
          <div className="digits" aria-live="polite">{value ? groupDigits(value) : "12 位数字"}</div>
          <p className="status">{value.length}/{KEY_LENGTH}</p>
          {notice ? <p className="notice">{notice}</p> : null}
          <DigitPad
            onDigit={(digit) => {
              setNotice("");
              if (!digit) {
                setValue("");
                return;
              }
              setValue((current) => (current.length >= KEY_LENGTH ? current : current + digit));
            }}
            onDelete={() => setValue((current) => current.slice(0, -1))}
          />
          <div className="foot">
            {clearing ? (
              <button type="button" className="ghost-btn" onClick={() => { setClearing(false); setClearDraft(""); setNotice(""); }}>
                返回
              </button>
            ) : null}
            <button
              type="button"
              className={clearing ? "danger-btn" : "next-btn"}
              disabled={value.length !== KEY_LENGTH}
              onClick={() => {
                if (!clearing) {
                  localStorage.setItem(KEY_STORAGE, draft);
                  setNotice("");
                  setKey(draft);
                  setDraft("");
                  setReady(false);
                  return;
                }
                if (clearDraft !== key) {
                  setClearDraft("");
                  setNotice("密钥不正确");
                  return;
                }
                forgetSavedKey("本机密钥已清除");
              }}
            >
              {clearing ? "确认清除" : "保存并开始"}
            </button>
          </div>
        </section>
      </Stage>
    );
  }

  if (!terminalName) {
    return (
      <Stage>
        <section className="solo">
          <h1>终端暂不可用</h1>
          <p className="notice">{notice || "无法确认终端状态"}</p>
          <p className="lead">密钥仍留在本机。恢复开放后即可继续。</p>
          <button type="button" className="ghost-btn" onClick={() => { setClearDraft(""); setNotice(""); setClearing(true); }}>
            清除本机密钥
          </button>
        </section>
      </Stage>
    );
  }

  if (receipt && member) {
    return (
      <Stage>
        <MemberMark member={member} />
        <header className="topbar">
          <strong>{terminalName}</strong>
        </header>
        <section className="solo done">
          <p className="kicker">结账完成</p>
          <h1>{member.name}</h1>
          <p className="lead">已从余额扣款</p>
          <dl className="facts">
            <div><dt>订单</dt><dd>{receipt.orderNumber}</dd></div>
            <div><dt>扣款</dt><dd className="spend">{money(receipt.payAmount)}</dd></div>
            <div><dt>余额</dt><dd className="after">{money(receipt.balance)}</dd></div>
          </dl>
          <div className="check-slot">
            <Confetti />
            <CheckPop />
          </div>
        </section>
        <button type="button" className="done-next" onClick={resetSale}>
          开始下一单
          <small>{returnLeft} 秒后返回</small>
        </button>
      </Stage>
    );
  }

  const memberSummary = member ? `${member.name} · ${money(member.balance)}` : digits ? groupDigits(digits) : "输入尾号";
  const goodsSummary = count > 0 ? `${count} 件 · ${money(total)}` : "点选商品";
  const paySummary = canCheckout ? money(total) : shortfall ? "余额不足" : "待确认";
  const tailSummary = payCode.length === PHONE_LENGTH ? "已输入" : "输入完整手机号";

  return (
    <Stage>
      {member ? <MemberMark member={member} hidePhone={step === 3} /> : null}
      <header className="topbar">
        <strong>{terminalName}</strong>
        <button type="button" onClick={() => { setClearDraft(""); setNotice(""); setClearing(true); }}>清除密钥</button>
      </header>
      <nav className="progress" data-step={step} aria-label="结账进度">
        {([
          ["会员", memberSummary, true],
          ["商品", goodsSummary, Boolean(member) || step >= 1],
          ["结账", paySummary, count > 0 || step >= 2],
          ["确认", tailSummary, (canCheckout && hasPhone(member)) || step >= 3],
        ] as const).map(([label, hint, enabled], index) => (
          <button
            key={label}
            type="button"
            className={index === step ? "current" : index < step ? "done" : ""}
            aria-current={index === step ? "step" : undefined}
            disabled={!enabled && index > step}
            onClick={() => go(index as SaleStep)}
          >
            <span>{index + 1} {label}</span>
            <small>{hint}</small>
          </button>
        ))}
      </nav>
      <div className="deck" data-step={step}>
        <section className={`card${step === 0 ? " open" : ""}`} aria-current={step === 0 ? "step" : undefined}>
          <button type="button" className="card-head" onClick={() => go(0)}>
            <span>1 会员</span>
            <small>{memberSummary}</small>
          </button>
          <div className="card-body" inert={step !== 0}>
            <div className="digits" aria-live="polite">{member ? member.name : digits || "会员号后 4 位"}</div>
            <p className={notice && !member ? "notice" : "status"}>
              {member
                ? `${member.accountLabel}${member.phoneLabel ? ` · ${member.phoneLabel}` : ""} · 余额 ${money(member.balance)}`
                : memberLookup === "loading"
                  ? "正在查找…"
                  : notice
                    ? notice
                    : digits.length >= 4 && memberLookup === "done" && members.length === 0
                      ? "没有匹配的会员"
                      : "输入至少 4 位，点选会员"}
            </p>
            {!member && members.length > 0 ? (
              <div className="hits">
                {members.map((item) => (
                  <button key={item.id} type="button" className="hit" onClick={() => { setMember(item); setMembers([]); setNotice(""); }}>
                    <strong>{item.name}</strong>
                    <span className="hit-phone">{item.phoneLabel || "未绑定手机"}</span>
                    <span className="hit-balance">{money(item.balance)}</span>
                  </button>
                ))}
              </div>
            ) : null}
            {member ? (
              <button type="button" className="ghost-btn change" onClick={() => { setMember(null); setDigits(""); }}>
                换一个会员
              </button>
            ) : (
              <DigitPad
                onDigit={(digit) => {
                  setNotice("");
                  if (!digit) {
                    setDigits("");
                    return;
                  }
                  setDigits((current) => (current.length >= 11 ? current : current + digit));
                }}
                onDelete={() => setDigits((current) => current.slice(0, -1))}
              />
            )}
            <div className="foot">
              <button type="button" className="next-btn" disabled={!member} onClick={() => go(1)}>
                去选商品
              </button>
            </div>
          </div>
        </section>

        <section className={`card${step === 1 ? " open" : ""}`} aria-current={step === 1 ? "step" : undefined}>
          <button type="button" className="card-head" disabled={!member && step < 1} onClick={() => go(1)}>
            <span>2 商品</span>
            <small>{goodsSummary}</small>
          </button>
          <div className="card-body" inert={step !== 1}>
            <div className="goods">
              {products.length === 0 ? <p className="status">暂无上架商品</p> : null}
              {products.map((product) => {
                const soldOut = product.trackInventory && BigInt(product.stock) <= 0n;
                const qty = quantityByProduct.get(product.id) ?? 0;
                return (
                  <article key={product.id} className={`good${qty > 0 ? " in" : ""}`}>
                    <button type="button" className="good-add" disabled={soldOut} onClick={() => addProduct(product)}>
                      <strong>{product.name}</strong>
                      <span className="price">{soldOut ? "售罄" : money(product.price)}</span>
                    </button>
                    {qty > 0 ? (
                      <div className="qty">
                        <button type="button" aria-label={`减少 ${product.name}`} onClick={() => changeQuantity(product.id, qty - 1)}>−</button>
                        <span>{qty}</span>
                        <button type="button" aria-label={`增加 ${product.name}`} onClick={() => changeQuantity(product.id, qty + 1)}>+</button>
                      </div>
                    ) : null}
                  </article>
                );
              })}
            </div>
            <div className="foot">
              <button type="button" className="ghost-btn" onClick={() => go(0)}>返回</button>
              <strong className="total">{count > 0 ? money(total) : "未选商品"}</strong>
              <button type="button" className="next-btn" disabled={count === 0} onClick={() => go(2)}>
                去确认
              </button>
            </div>
          </div>
        </section>

        <section className={`card${step === 2 ? " open" : ""}`} aria-current={step === 2 ? "step" : undefined}>
          <button type="button" className="card-head" disabled={count === 0 && step < 2} onClick={() => go(2)}>
            <span>3 结账</span>
            <small>{paySummary}</small>
          </button>
          <div className="card-body" inert={step !== 2}>
            {member ? (
              <>
                <p className="lead">{member.name} · 余额 {money(member.balance)}</p>
                <ul className="bill">
                  {lines.map((line) => (
                    <li key={line.productId}>
                      <span>{line.name} × {line.quantity}</span>
                      <span>{money(line.price * BigInt(line.quantity))}</span>
                    </li>
                  ))}
                </ul>
                <dl className="facts">
                  <div><dt>合计</dt><dd className="spend">{money(total)}</dd></div>
                  <div><dt>扣款后</dt><dd className={shortfall ? "spend" : "after"}>{money(balance - total)}</dd></div>
                </dl>
                {shortfall ? <p className="notice">余额不够，请返回减少商品。</p> : null}
                {hasPhone(member) ? null : <p className="notice">该会员未绑定手机，无法在此确认付款。</p>}
                <div className="foot">
                  <button type="button" className="ghost-btn" onClick={() => go(1)}>返回改商品</button>
                  <button
                    type="button"
                    className="next-btn"
                    disabled={!canCheckout || !hasPhone(member)}
                    onClick={() => go(3)}
                  >
                    结账
                  </button>
                </div>
              </>
            ) : null}
          </div>
        </section>

        <section className={`card${step === 3 ? " open" : ""}`} aria-current={step === 3 ? "step" : undefined}>
          <button type="button" className="card-head" disabled={(!canCheckout || !hasPhone(member)) && step < 3} onClick={() => go(3)}>
            <span>4 确认</span>
            <small>{tailSummary}</small>
          </button>
          <div className="card-body" inert={step !== 3}>
            {member ? (
              <>
                <p className="lead">输入 {member.name} 的完整手机号，核对后再扣款 {money(total)}</p>
                {hasPhone(member) ? (
                  <>
                    <div className="digits" aria-live="polite">{payCode ? groupPhone(payCode) : "完整手机号"}</div>
                    <DigitPad
                      onDigit={(digit) => {
                        if (!digit) {
                          setPayCode("");
                          return;
                        }
                        setPayCode((current) => (current.length >= PHONE_LENGTH ? current : current + digit));
                      }}
                      onDelete={() => setPayCode((current) => current.slice(0, -1))}
                    />
                  </>
                ) : (
                  <p className="notice">该会员未绑定手机，无法在此确认付款。</p>
                )}
                {notice ? <p className="notice">{notice}</p> : null}
                <div className="foot">
                  <button type="button" className="ghost-btn" disabled={submitting} onClick={() => go(2)}>返回结账</button>
                  <button
                    type="button"
                    className="next-btn"
                    disabled={!canCheckout || submitting || !/^1\d{10}$/.test(payCode)}
                    onClick={() => void checkout()}
                  >
                    {submitting ? "正在扣款…" : "确认扣款"}
                  </button>
                </div>
              </>
            ) : null}
          </div>
        </section>
      </div>
    </Stage>
  );
}
